/**
 * ============================================================================
 * SHIPPING ESTIMATE WIDGET — JavaScript
 * ============================================================================
 * Added April 2026
 *
 * WHAT THIS DOES:
 * Powers the delivery estimate widget on every product page. Handles the
 * entire customer-facing interaction:
 *   - Auto-loads with saved zip from localStorage (returning visitors)
 *   - Auto-requests browser geolocation on first visit (silent if denied)
 *   - Reverse-geocodes lat/lng → zip via OpenStreetMap (free, no API key)
 *   - Lets customer manually enter a zip
 *   - Calls our backend API to fetch the actual estimate
 *   - Re-fetches when customer switches variants
 *   - Persists the customer's zip across pages and sessions
 *   - Hides itself gracefully on any backend failure
 *
 * BACKEND:
 *   GET /api/public/shipping-estimate?variant_id={id}&zip={zip}
 *   See backend repo:
 *     - routes/v1/public.php
 *     - app/Http/Controllers/Public/ShippingEstimateController.php
 *     - app/Services/ShippingEstimateService.php
 *
 * RENDERED FROM:
 *   snippets/shipping-estimate.liquid (which is included by snippets/product-form.liquid)
 *
 * ERROR HANDLING:
 *   Any failure (network error, backend 5xx, invalid response, missing
 *   variant in backend) results in the widget hiding silently. The customer
 *   never sees an error message — the page continues to function normally.
 *
 * BROWSER SUPPORT:
 *   Modern browsers only (uses fetch, localStorage, navigator.geolocation).
 *   Gracefully no-ops on unsupported browsers.
 *
 * TECH TEAM NOTES:
 *   - The API_URL constant below MUST be set to the production backend URL
 *     before pushing this file to the live theme. Currently uses a hostname
 *     check that defaults to backend.tipxy.com on production domains.
 *   - The reverse-geocoding service (Nominatim) is rate-limited to ~1 req/sec
 *     per IP. We only call it once per geolocation grant, so this is fine
 *     under normal traffic.
 *   - localStorage key: `tipxy_zip` — clearing this resets the customer's saved zip.
 *   - The widget listens to two events for variant change:
 *       'variant:change' (Shopify default)
 *       'product:variantChange' (Empire theme custom)
 *     Plus a fallback `change` listener on the form for the hidden `[name=id]` input.
 *
 * @namespace tipxy.shippingEstimate
 */
(function () {

  // ==========================================================================
  // DISPLAY SPREAD CONFIG
  // ==========================================================================
  // Compresses the displayed delivery range to the last N business days.
  // Backend returns the full range (e.g., May 4 - May 11); JS clamps the
  // shown min so the customer sees only the last N business days
  // (e.g., DISPLAY_SPREAD_DAYS=1 → "May 8 - May 11", weekend in middle).
  // Set to 0 to show only the max date.
  // To change: edit this constant and save the asset — no backend deploy needed.
  var DISPLAY_SPREAD_DAYS = 2;

  // US shipping holidays (mirrors backend config/shipping.php).
  // UPDATE ANNUALLY — keep in sync with the backend list.
  var US_HOLIDAYS = [
    '2026-01-01', '2026-01-19', '2026-02-16', '2026-05-25', '2026-06-19',
    '2026-07-03', '2026-09-07', '2026-10-12', '2026-11-11', '2026-11-26',
    '2026-12-25',
    '2027-01-01', '2027-01-18', '2027-02-15', '2027-05-31', '2027-06-18',
    '2027-07-05', '2027-09-06', '2027-10-11', '2027-11-11', '2027-11-25',
    '2027-12-24',
  ];

  // Day-of-week + holiday check
  function _isBusinessDay(date) {
    var dow = date.getDay();
    if (dow === 0 || dow === 6) return false; // Sun/Sat
    var iso =
      date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
    return US_HOLIDAYS.indexOf(iso) === -1;
  }

  // Walk backward N business days from the given date (skipping weekends + holidays)
  function _subtractBusinessDays(date, n) {
    var d = new Date(date.getTime());
    while (n > 0) {
      d.setDate(d.getDate() - 1);
      if (_isBusinessDay(d)) n--;
    }
    return d;
  }

  // Format a Date as "Fri, May 8" to match the backend's formatting
  function _formatDateLabel(date) {
    var days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return days[date.getDay()] + ', ' + months[date.getMonth()] + ' ' + date.getDate();
  }

  // Apply the display-spread clamp to a backend response in place.
  // Mutates data.estimated_delivery_min, data.estimated_delivery_min_label, data.message.
  function _applyDisplaySpread(data) {
    if (!data || !data.has_zip || !data.estimated_delivery_max) return;
    // Parse the max date (YYYY-MM-DD) — split to avoid UTC parsing issues
    var parts = String(data.estimated_delivery_max).split('-');
    if (parts.length !== 3) return;
    var maxDate = new Date(parseInt(parts[0],10), parseInt(parts[1],10) - 1, parseInt(parts[2],10));
    var newMin = _subtractBusinessDays(maxDate, DISPLAY_SPREAD_DAYS);
    // Don't go before today's date — defensive
    var today = new Date();
    today.setHours(0,0,0,0);
    if (newMin < today) newMin = today;
    var newMinIso =
      newMin.getFullYear() + '-' +
      String(newMin.getMonth() + 1).padStart(2,'0') + '-' +
      String(newMin.getDate()).padStart(2,'0');
    var newMinLabel = _formatDateLabel(newMin);
    data.estimated_delivery_min = newMinIso;
    data.estimated_delivery_min_label = newMinLabel;
    if (data.message && data.estimated_delivery_max_label) {
      // Replace the date range in the message: "...: <oldMin> - <oldMax>..." → "...: <newMin> - <oldMax>..."
      data.message = data.message.replace(
        /Estimated delivery:\s*[^-]+-\s*/i,
        'Estimated delivery: ' + newMinLabel + ' - '
      );
    }
  }


  'use strict';

  // ==========================================================================
  // CONFIG
  // ==========================================================================

  /**
   * Backend API URL. Auto-detects environment based on hostname:
   *   - On production (any non-localhost): hits backend.tipxy.com
   *   - On localhost: hits local Docker backend on port 9000
   *
   * TECH TEAM: when deploying, ensure backend.tipxy.com has the new endpoint
   * available (see backend repo PR for the shipping-estimate route).
   */
  var API_URL = (function () {
    var host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:9000/api/public/shipping-estimate';
    }
    return 'https://backend.tipxy.com/api/public/shipping-estimate';
  })();

  /** localStorage key used to persist the customer's zip across pages. */
  var ZIP_STORAGE_KEY = 'tipxy_zip';

  /** Geolocation request timeout (ms). Browser will give up after this. */
  var GEOLOCATION_TIMEOUT = 8000;

  /** How long to accept a cached browser position (ms). 10 minutes. */
  var GEOLOCATION_MAX_AGE = 600000;

  // ==========================================================================
  // ENTRY POINT
  // ==========================================================================

  /**
   * Find all widget instances on the page and wire them up.
   * Called on DOMContentLoaded.
   */
  function init() {
    var widgets = document.querySelectorAll('[data-ship-est]');
    if (widgets.length === 0) return;
    widgets.forEach(setupWidget);
  }

  /**
   * Wire up a single widget instance:
   *  - On page load: try saved zip → otherwise auto-request geolocation
   *  - Bind manual zip entry handlers
   *  - Bind geolocation button
   *  - Bind "change zip" button
   *  - Listen for variant change events to re-fetch
   *
   * @param {HTMLElement} widget The .ship-est container element
   */
  function setupWidget(widget) {
    // Cache DOM references (avoids re-querying on every interaction)
    var el = {
      widget: widget,
      prompt: widget.querySelector('[data-ship-est-prompt]'),
      loading: widget.querySelector('[data-ship-est-loading]'),
      result: widget.querySelector('[data-ship-est-result]'),
      zipInput: widget.querySelector('[data-ship-est-zip-input]'),
      zipSubmit: widget.querySelector('[data-ship-est-zip-submit]'),
      geoBtn: widget.querySelector('[data-ship-est-geo]'),
      message: widget.querySelector('[data-ship-est-message]'),
      zipInfo: widget.querySelector('[data-ship-est-zip-info]'),
      disclaimer: widget.querySelector('[data-ship-est-disclaimer]'),
      changeBtn: widget.querySelector('[data-ship-est-change]'),
    };

    // INITIAL LOAD STRATEGY:
    //  1. If saved zip in localStorage → fetch estimate immediately
    //  2. Otherwise → show prompt + silently ask for geolocation
    var savedZip = readSavedZip();
    if (savedZip) {
      fetchEstimate(el, getVariantId(widget), savedZip);
    } else {
      showPromptOnly(el);
      requestGeolocationSilently(el, widget);
    }

    // Manual zip entry — Submit button
    el.zipSubmit.addEventListener('click', function () {
      handleManualZip(el, widget);
    });

    // Manual zip entry — Enter key
    el.zipInput.addEventListener('keypress', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleManualZip(el, widget);
      }
    });

    // Strip non-numeric chars and cap length as user types
    el.zipInput.addEventListener('input', function () {
      this.value = this.value.replace(/\D/g, '').slice(0, 5);
    });

    // "Use my location" button — manual geolocation request
    el.geoBtn.addEventListener('click', function () {
      handleGeolocation(el, widget);
    });

    // "Change zip" button — clear saved zip, reset to prompt
    el.changeBtn.addEventListener('click', function () {
      saveZip(null);
      el.widget.classList.remove('ship-est--compact');
      el.result.style.display = 'none';
      el.prompt.style.display = 'flex';
      el.zipInput.focus();
    });

    // Listen for variant change to re-fetch estimate with new variant ID
    listenForVariantChange(widget, el);
  }

  // ==========================================================================
  // STATE MANAGEMENT
  // ==========================================================================

  /** Get the currently selected Shopify variant ID from the widget. */
  function getVariantId(widget) {
    return widget.getAttribute('data-variant-id');
  }

  /**
   * Read the saved zip from localStorage. Returns null if not set or invalid.
   * Wrapped in try/catch because localStorage can throw in private browsing.
   */
  function readSavedZip() {
    try {
      var z = localStorage.getItem(ZIP_STORAGE_KEY);
      return /^\d{5}$/.test(z) ? z : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save (or clear) the customer's zip in localStorage.
   * Wrapped in try/catch because localStorage can throw in private browsing.
   *
   * @param {string|null} zip 5-digit zip to save, or null to clear
   */
  function saveZip(zip) {
    try {
      if (zip) {
        localStorage.setItem(ZIP_STORAGE_KEY, zip);
      } else {
        localStorage.removeItem(ZIP_STORAGE_KEY);
      }
    } catch (e) { /* private browsing — ignore silently */ }
  }

  // ==========================================================================
  // UI STATE TRANSITIONS
  // ==========================================================================

  /**
   * Show only the zip prompt (no API call, no result panel).
   * Used on initial page load before we have a zip from any source.
   */
  function showPromptOnly(el) {
    el.widget.classList.remove('ship-est--compact');
    el.loading.style.display = 'none';
    el.result.style.display = 'none';
    el.prompt.style.display = 'flex';
    var promptText = el.prompt.querySelector('.ship-est__prompt-text');
    if (promptText) {
      promptText.innerHTML =
        '<strong>Enter zip code for delivery estimate</strong>' +
        '<span class="ship-est__prompt-sub">Or share your location to auto-fill</span>';
    }
  }

  // ==========================================================================
  // INTERACTION HANDLERS
  // ==========================================================================

  /**
   * Validate and submit a manually-entered zip code.
   * Flashes the input red on invalid input, otherwise saves + fetches.
   */
  function handleManualZip(el, widget) {
    var zip = (el.zipInput.value || '').trim();
    if (!/^\d{5}$/.test(zip)) {
      // Brief visual error feedback (no disruptive alert)
      el.zipInput.style.borderColor = '#f04f36';
      setTimeout(function () { el.zipInput.style.borderColor = ''; }, 1500);
      return;
    }
    saveZip(zip);
    fetchEstimate(el, getVariantId(widget), zip);
  }

  /**
   * Manually-triggered geolocation request (from the "Use location" button).
   * Shows visible loading state on the button itself.
   */
  function handleGeolocation(el, widget) {
    if (!navigator.geolocation) return;

    el.geoBtn.disabled = true;
    el.geoBtn.textContent = 'Locating...';

    navigator.geolocation.getCurrentPosition(
      function (pos) {
        reverseGeocode(pos.coords.latitude, pos.coords.longitude)
          .then(function (zip) {
            resetGeoButton(el);
            if (zip) {
              saveZip(zip);
              fetchEstimate(el, getVariantId(widget), zip);
            }
          })
          .catch(function () {
            resetGeoButton(el);
          });
      },
      function () {
        // Permission denied or position unavailable
        resetGeoButton(el);
      },
      { timeout: GEOLOCATION_TIMEOUT, maximumAge: GEOLOCATION_MAX_AGE }
    );
  }

  /**
   * Auto-request geolocation on page load — silent on denial.
   * Used when no zip is saved yet, to try to auto-fill without forcing
   * the customer to type. The browser shows its native permission dialog.
   */
  function requestGeolocationSilently(el, widget) {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      function (pos) {
        reverseGeocode(pos.coords.latitude, pos.coords.longitude)
          .then(function (zip) {
            if (zip) {
              saveZip(zip);
              fetchEstimate(el, getVariantId(widget), zip);
            }
          })
          .catch(function () { /* silent — user can still type zip */ });
      },
      function () { /* permission denied — stay on prompt silently */ },
      { timeout: GEOLOCATION_TIMEOUT, maximumAge: GEOLOCATION_MAX_AGE }
    );
  }

  /** Reset the geolocation button to its default state. */
  function resetGeoButton(el) {
    el.geoBtn.disabled = false;
    el.geoBtn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>' +
      '<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>' +
      '</svg> Use location';
  }

  // ==========================================================================
  // GEOCODING
  // ==========================================================================

  /**
   * Reverse-geocode lat/lng to a 5-digit US zip code via Nominatim
   * (OpenStreetMap's free service — no API key required).
   *
   * @param {number} lat Latitude
   * @param {number} lng Longitude
   * @returns {Promise<string|null>} Resolves to 5-digit zip, or null if unavailable
   */
  function reverseGeocode(lat, lng) {
    return fetch(
      'https://nominatim.openstreetmap.org/reverse?format=json' +
      '&lat=' + encodeURIComponent(lat) +
      '&lon=' + encodeURIComponent(lng) +
      '&zoom=10',
      { headers: { 'Accept': 'application/json' } }
    )
      .then(function (r) {
        if (!r.ok) throw new Error('Geocoding failed');
        return r.json();
      })
      .then(function (data) {
        var zip = data && data.address && data.address.postcode;
        if (!zip) return null;
        // Nominatim sometimes returns ZIP+4 — strip to first 5 digits
        var match = String(zip).match(/^\d{5}/);
        return match ? match[0] : null;
      });
  }

  // ==========================================================================
  // BACKEND API
  // ==========================================================================

  /**
   * Fetch a shipping estimate from our backend and render the result.
   *
   * On any failure (network error, non-OK response, success: false in body),
   * the widget hides itself silently — the customer never sees an error.
   *
   * @param {object} el Cached DOM references for this widget instance
   * @param {string} variantId The current Shopify variant ID
   * @param {string|null} zip Customer zip code, or null for processing-only estimate
   */
  function fetchEstimate(el, variantId, zip) {
    if (!variantId) return;

    // Show loading state
    el.widget.classList.remove('ship-est--compact');
    el.prompt.style.display = 'none';
    el.result.style.display = 'none';
    el.loading.style.display = 'flex';

    var url = API_URL + '?variant_id=' + encodeURIComponent(variantId);
    if (zip) url += '&zip=' + encodeURIComponent(zip);

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Backend returned ' + r.status);
        return r.json();
      })
      .then(function (data) {
        el.loading.style.display = 'none';
        if (!data || data.success === false || !data.message) {
          // Hide gracefully — never show an error to the customer
          el.widget.classList.add('ship-est--hidden');
          return;
        }
        renderResult(el, data, zip);
      })
      .catch(function () {
        // Network error, backend 5xx, malformed JSON — all handled the same way
        el.loading.style.display = 'none';
        el.widget.classList.add('ship-est--hidden');
      });
  }

  /**
   * Render the final estimate in the widget UI.
   *
   * Two display modes:
   *   - has_zip: full date range like "Estimated delivery: Mon, Apr 21 - Wed, Apr 23"
   *   - !has_zip: fallback to prompt-only (defensive — shouldn't normally happen here)
   */
  function renderResult(el, data, zip) {
    // Hide entirely if the variant is OOS + not set to continue selling.
    // Backend signals this via in_stock=false && needs_restock=false.
    // (needs_restock is only true when continue_selling is enabled and stock=0.)
    if (data.in_stock === false && data.needs_restock === false) {
      el.widget.classList.add('ship-est--hidden');
      el.prompt.style.display = 'none';
      el.loading.style.display = 'none';
      el.result.style.display = 'none';
      return;
    }
    // Apply frontend-side display compression before formatting (last N business days)
    _applyDisplaySpread(data);
    // Defensive: if backend returned a no-zip response somehow, fall back to prompt
    if (!data.has_zip) {
      showPromptOnly(el);
      return;
    }

    // Bold the date range portion of the message for visual emphasis
    var msg = data.message || '';
    var formatted = msg.replace(/(Estimated delivery:)\s*(.*)/i, '$1 <strong>$2</strong>');
    el.message.innerHTML = formatted;
    el.zipInfo.textContent = 'Shipping to ' + zip;
    el.disclaimer.textContent = data.disclaimer || '';
    el.prompt.style.display = 'none';
    el.result.style.display = 'flex';
    el.widget.classList.add('ship-est--compact');
    el.widget.classList.remove('ship-est--hidden');
  }

  /** Escape HTML to prevent XSS when rendering server-provided strings. */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  // ==========================================================================
  // VARIANT CHANGE LISTENER
  // ==========================================================================

  /**
   * Listen for variant changes so we can re-fetch the estimate.
   *
   * Different themes dispatch different events — we listen to all of them:
   *  - Empire theme: dispatches 'variant:change' or 'product:variantChange'
   *  - Generic Shopify themes: update the form's hidden [name=id] input on change
   *
   * The form-level `change` listener is the reliable fallback that catches
   * any theme that updates the hidden input via JS (which all themes do).
   */
  function listenForVariantChange(widget, el) {
    var form = widget.closest('form');
    if (!form) return;

    var lastVariantId = getVariantId(widget);

    var refreshIfChanged = function () {
      var hidden = form.querySelector('[name="id"]');
      var newId = hidden ? hidden.value : null;
      if (newId && newId !== lastVariantId) {
        lastVariantId = newId;
        widget.setAttribute('data-variant-id', newId);
        fetchEstimate(el, newId, readSavedZip());
      }
    };

    form.addEventListener('change', refreshIfChanged);
    document.addEventListener('variant:change', refreshIfChanged);
    document.addEventListener('product:variantChange', refreshIfChanged);
  }

  // ==========================================================================
  // BOOTSTRAP
  // ==========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();